import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import priceService from '../api/priceService';
import elementService from '../api/elementService';
import AuthContext from './AuthContext'; // <-- Добавлено: импорт AuthContext

const PricingContext = createContext();

export const usePricing = () => useContext(PricingContext);

export const PricingProvider = ({ children }) => {
  const [prices, setPrices] = useState({ MODULES: {}, ELEMENTS: {} });
  const [templates, setTemplates] = useState([]); // Хранение шаблонов с ценами
  const [isLoading, setIsLoading] = useState(true);
  const [pricesLoaded, setPricesLoaded] = useState(false); // Флаг для отслеживания загрузки цен
  const { isAuthenticated } = useContext(AuthContext); // <-- Добавлено: получаем статус авторизации

  const fetchPrices = useCallback(async (forceReload = false) => {
    // <-- Изменено: НЕ сбрасываем цены, если пользователь не авторизован
    // Просто не загружаем новые цены, но сохраняем существующие
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    // Если цены уже загружены и не требуется принудительная перезагрузка, пропускаем
    if (pricesLoaded && !forceReload) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Загружаем цены элементов и модулей по типам
      const pricesResponse = await priceService.getPublicPrices();
      let finalPrices = pricesResponse.data || { MODULES: {}, ELEMENTS: {} };
      
      // Загружаем кастомные элементы из БД и добавляем их цены
      try {
        const customElementsResponse = await elementService.getElementsForConfigurator();
        const customElements = customElementsResponse.data || [];
        
        // Добавляем цены кастомных элементов в ELEMENTS по их elementCode
        customElements.forEach(element => {
          if (element.elementCode && element.price) {
            finalPrices.ELEMENTS[element.elementCode] = parseFloat(element.price);
          }
        });
        
      } catch (error) {
        // Ошибка при загрузке кастомных элементов
      }
      
      setPrices(finalPrices);

      // Загружаем шаблоны с индивидуальными ценами
      const templateService = require('../api/templateService').default;
      const templatesResponse = await templateService.getPublicTemplates();
      const parsedTemplates = templatesResponse.data.map((tpl) => ({
        ...tpl,
        config: typeof tpl.config === 'string' ? JSON.parse(tpl.config) : tpl.config,
      }));
      setTemplates(parsedTemplates);
      setPricesLoaded(true); // Устанавливаем флаг после успешной загрузки
    } catch (error) {
      // Ошибка при загрузке цен с сервера
      // НЕ сбрасываем цены при ошибке, оставляем существующие
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, pricesLoaded]); // <-- Добавляем pricesLoaded в зависимости

  // Отслеживаем изменение API URL и сбрасываем кеш
  useEffect(() => {
    const handleApiUrlChange = () => {
      // API URL изменился, сбрасываем кеш цен
      // Сбрасываем флаг загрузки и очищаем данные
      setPricesLoaded(false);
      setPrices({ MODULES: {}, ELEMENTS: {} });
      setTemplates([]);
      // Принудительно перезагружаем данные
      fetchPrices(true);
    };

    window.addEventListener('api-url-changed', handleApiUrlChange);
    return () => {
      window.removeEventListener('api-url-changed', handleApiUrlChange);
    };
  }, [fetchPrices]); // fetchPrices в зависимостях

  // Проверяем при монтировании, не изменился ли API URL
  useEffect(() => {
    const CURRENT_API_URL_KEY = 'current_api_base_url';
    const currentApiUrl = process.env.REACT_APP_API_BASE_URL;
    const storedApiUrl = localStorage.getItem(CURRENT_API_URL_KEY);
    
    // Если API URL изменился с момента последней загрузки, сбрасываем кеш
    if (storedApiUrl && storedApiUrl !== currentApiUrl) {
      // API URL изменился при монтировании, сбрасываем кеш цен
      setPricesLoaded(false);
      setPrices({ MODULES: {}, ELEMENTS: {} });
      setTemplates([]);
    }
  }, []); // Только при монтировании

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]); // Теперь этот эффект будет перезапускаться при изменении статуса авторизации

  const updatePrices = async (newPrices) => {
    // Форматируем данные для бэкенда
    const payload = {
      modules: newPrices.MODULES,
      elements: newPrices.ELEMENTS,
    };
    console.log('🚀 [PricingContext] Відправляємо на сервер payload:', payload);
    await priceService.updateAdminPrices(payload);
    console.log('✅ [PricingContext] Відповідь отримано, оновлюємо ціни...');
    await fetchPrices(true); // Обновляем цены после сохранения с принудительной перезагрузкой
    console.log('✅ [PricingContext] Ціни оновлено');
  };

  const value = {
    prices,
    templates,
    updatePrices,
    isLoading,
    refetch: fetchPrices,
  };

  return (
    <PricingContext.Provider value={value}>
      {isLoading ? (
        <div className="min-h-screen flex items-center justify-center bg-light-bg text-gray-700">
            Завантаження даних...
        </div>
      ) : (
        children
      )}
    </PricingContext.Provider>
  );
};

export default PricingContext;